import {
  Box
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useClient } from '@/api/backoffice-api';
import AppConfig from '@/utils/app-config.mjs';
import {
  getLicenseByZipDistance,
  getLicenseByZipQueryParams
} from '../ducks/selectors';
import { FFLDealerSearchGrid } from "@masterffl/ffl-grid-component"
import { useNavigate } from 'react-router';
import { Recognizer, ZipCodeRecognizer } from '@masterffl/ffl-search-component';
import FFLMapView from './FFLMapView';
import { setIsSearchStarted, setIsSearchingDealer, setSearchCount } from '../ducks/slice';
import { useDispatch } from 'react-redux';

interface Props {
  query: string;
  cognitoGroup: boolean;
  isSuperAdmin: boolean;
  searchRecognizer: Recognizer;
  selectedView: string;
  isFFLSelect?: boolean;
  onDealerAssigned?(arg: any): void;
}

enum SortType {
  'Preferred' = 'preferred',
  'Distance' = 'distance',
  'Rating' = 'rating',
  'Name' = 'name'
}

type SortTypeKeyType = keyof typeof SortType;

const sortTypes = ['01', '02', '03', '06', '07', '08', '09', '10', '11'];

export default function DealerCardResults({
  query,
  cognitoGroup,
  isSuperAdmin,
  searchRecognizer,
  selectedView,
  isFFLSelect,
  onDealerAssigned
}: Props) {

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [componentData, setComponentData] = useState({
    nextPage: false,
    currentPageVal: 1,
    dealers: [],
    loadingData: false,
    errorData: false,
  });
  const [dealerData, setDealerData] = useState<any[]>([]);
  const [pageSize, setPageSize] = useState(20);
  const [pageNumber, setPageNumber] = useState(1);
  const [containerHeight, setContainerHeight] = useState('');
  const getDistance = getLicenseByZipDistance();
  const getLicenseByZipParams = getLicenseByZipQueryParams();
  const [{ loading, error, data }] = useClient({
    baseURL: AppConfig.fflGetApiBaseURL,
    url: `/v2/licenses`,
    timeout: 60000,
    params: {
      ...getLicenseByZipParams,
      types: getLicenseByZipParams?.types
        ? getLicenseByZipParams?.types
        : sortTypes.toString(),
      ...(searchRecognizer instanceof ZipCodeRecognizer ? { zipCode: query, distance: getDistance } : { business_name: query }),
      page_size: pageSize,
      page_number: pageNumber
    }
  });

  useEffect(() => {
    const detailsData = {
      ...componentData,
      dealers: [],
      loadingData: loading,
    }
    setComponentData(detailsData)
    setDealerData([])
    setPageNumber(1)
  }, [getDistance, getLicenseByZipParams, query]);

  useEffect(() => {
    const detailsData = {
      ...componentData,
      errorData: false,
      loadingData: loading
    }
    if (loading) dispatch(setIsSearchStarted(true));
    setComponentData(detailsData)
    dispatch(setIsSearchingDealer(true));
  }, [loading]);

  useEffect(() => {
    if (error) {
      clearData(true);
    }
    dispatch(setIsSearchingDealer(false));
  }, [error]);

  const clearData = (dataNotFound = false) => {
    const detailsData = {
      nextPage: false,
      currentPageVal: 1,
      dealers: [],
      loadingData: !dataNotFound,
      errorData: dataNotFound,
    }
    dispatch(setSearchCount(-1));
    if(dataNotFound) dispatch(setIsSearchStarted(false))
    setComponentData(detailsData)
    setDealerData([])
    setPageNumber(1)
  }
  useEffect(() => {
    if (data && data.dealers) {
      const dealerList = [...dealerData, ...data.dealers];
      const detailsData: any = {
        nextPage: data.meta.hasMore,
        currentPageVal: pageNumber,
        dealers: dealerList,
        loadingData: false,
        errorData: false,
      }
      dispatch(setSearchCount(Number(data?.meta?.total_count)));
      dispatch(setIsSearchStarted(false))
      setDealerData(dealerList)
      setComponentData(detailsData)
    } else if (!error && !loading) {
      const detailsData: any = {
        nextPage: false,
        currentPageVal: pageNumber,
        dealers: [],
        loadingData: false,
        errorData: false,
      }
      dispatch(setSearchCount(-1));
      dispatch(setIsSearchStarted(false))
      setDealerData([])
      setComponentData(detailsData)
    }
    dispatch(setIsSearchingDealer(false));
  }, [data]);


  const onFetchData = (args: any, isClearData = false) => {
    let currentPageSize = 0;
    let currentPageNumber = 0;
    setPageSize((currentValue) => {
      currentPageSize = currentValue
      return currentValue
    })
    setPageNumber((currentValue) => {
      currentPageNumber = currentValue
      return currentValue
    })
    if (isClearData && args.pageSize !== currentPageSize) {
      clearData()
    }
    if (args.nextPage !== currentPageNumber) {
      setPageNumber(args.nextPage == 0 ? 1 : args.nextPage)
    }
    if (args.pageSize !== currentPageSize) {
      setPageSize(args.pageSize)
    }
  }

  const onSelectCard = (licenseKey: string) => {
    const first1 = licenseKey.slice(0, 1);
    const second2 = licenseKey.slice(2, 4);
    const last5 = licenseKey.slice(-5);
    navigate(`/license-details/${first1}${second2}/${last5}`);
  };


  useEffect(() => {
    // Function to update container height
    function updateContainerHeight() {
      const element = document.querySelector('.search-custom-chip-box');

      const chipHeight = element && element.clientHeight ? element.clientHeight : 0;
      const notFound = document.getElementById('grid-component');
      const height = `calc(100vh - ${340 + chipHeight}px)`
      setContainerHeight(height)
      if (notFound) {
        notFound.style.minHeight = height
      }
    }

    // Update container height on initial load
    updateContainerHeight();

    // Add event listener for window resize
    window.addEventListener('resize', updateContainerHeight);

    // Remove event listener on component unmount
    return () => {
      window.removeEventListener('resize', updateContainerHeight);
    };
  }, [getDistance, getLicenseByZipParams, query]);

  const onAssignDealer = (dealer: any) => {
    if(onDealerAssigned) onDealerAssigned(dealer.id);
  }

  function displayResults() {
    const { sortBy, descending, ...rest } = getLicenseByZipParams
    const mediaQuery = window.matchMedia('(max-width: 743px)');
    return (
      <>
        <Box sx={{ display: selectedView == 'list' ? 'block' : 'none', height: '100%' }}>
        {selectedView == 'list' && (
          <FFLDealerSearchGrid
            dataList={componentData}
            clickHandler={isFFLSelect ? onAssignDealer : onSelectCard}
            fetchData={onFetchData}
            maxHeight={containerHeight}
            licenseByZipParams={getLicenseByZipParams}
            isFFLSelect={isFFLSelect}
            btnTitle={isFFLSelect ? "Select" : null}
            isBusinessTypeLeft={isFFLSelect}
            isCardV2={true}
          />
        )}
        </Box>
        {(selectedView === 'map') && (
          <>
            <FFLMapView
              query={query}
              cognitoGroup={cognitoGroup}
              isSuperAdmin={isSuperAdmin}
              searchRecognizer={searchRecognizer}
              containerHeight={containerHeight}
              isReAssignDealer={isFFLSelect}
              onDealerAssigned={onAssignDealer}
            />
          </>
        )}
      </>
    );
  }

  return (
    <Box id="result-container" className={'ZipCodeResults gird-result'}>
      {displayResults()}
    </Box>
  );
}
